import { Template } from 'modules/core/domain/interfaces/supabase/overlay/entity';
import {
  boardTemplateFileName,
  buildBoardTemplateFile,
  serializeBoardTemplateFile,
} from 'modules/template-editor/domain/template-file';
import sortBoards from 'modules/template-editor/domain/utils/sort-boards';
import { useLazyGetBoardTemplateBoardsQuery } from 'modules/template-editor/store/board-templates-api';
import { useCallback, useState } from 'react';
import { downloadTextFile } from 'shared/utils/download-text-file';

export interface UseExportBoardTemplateResult {
  isExporting: boolean;
  /**
   * Скачивает шаблон со всеми табло одним файлом. Отклоняется, если табло не прочитались, — тост показывает
   * вызывающий, файл не скачивается.
   */
  exportBoardTemplate: (template: Template) => Promise<void>;
}

/**
 * Экспорт шаблона трансляции в `<имя>.imgame-board-template.json` (IMGM-1589). Табло читаются заново (lazy-запрос без `preferCacheValue`):
 * их правят через слайс, и запись RTK Query о правках не знает. Порядок — как на экране (`sortBoards`: избранные
 * вперёд, затем `order`, при равном — дата создания), `order` перенумерован 1..n: избранность и даты в файл не
 * входят, и без перенумерации импортированный список выглядел бы иначе.
 */
export function useExportBoardTemplate(): UseExportBoardTemplateResult {
  const [readBoards] = useLazyGetBoardTemplateBoardsQuery();
  const [isExporting, setIsExporting] = useState(false);

  const exportBoardTemplate = useCallback(
    async (template: Template) => {
      setIsExporting(true);
      try {
        const boards = await readBoards({ templateId: template.id }, false).unwrap();
        const ordered = sortBoards([...boards]).map((board, index) => ({ ...board, order: index + 1 }));
        const file = buildBoardTemplateFile(template, ordered, {
          origin: window.location.origin,
          exportedAt: new Date().toISOString(),
        });
        downloadTextFile(serializeBoardTemplateFile(file), boardTemplateFileName(template));
      } finally {
        setIsExporting(false);
      }
    },
    [readBoards],
  );

  return { isExporting, exportBoardTemplate };
}
